import api from "./api";
import { buildGenreNames, getTrailerUrl, getReleaseYear } from "../utils/movie";

const CURATED_GENRES = [
  { slug: "action", title: "Acao sem Freio", genreName: "Action" },
  { slug: "comedy", title: "Comedias para Relaxar", genreName: "Comedy" },
  { slug: "horror", title: "Noite de Terror", genreName: "Horror" },
  { slug: "science-fiction", title: "Sci-Fi Essencial", genreName: "Science Fiction" },
  { slug: "animation", title: "Para Ver em Familia", genreName: "Animation" },
  { slug: "documentary", title: "Documentarios em Alta", genreName: "Documentary" },
];

function mapGenresById(genres = []) {
  return genres.reduce((accumulator, genre) => {
    accumulator[genre.id] = genre.name;
    return accumulator;
  }, {});
}

function pickTrailer(videos = []) {
  const preferredTrailer = videos.find(
    (video) =>
      video.site === "YouTube" &&
      video.type === "Trailer" &&
      /official|oficial/i.test(video.name)
  );

  if (preferredTrailer) {
    return {
      ...preferredTrailer,
      url: getTrailerUrl(preferredTrailer),
    };
  }

  const fallbackTrailer = videos.find(
    (video) => video.site === "YouTube" && ["Trailer", "Teaser"].includes(video.type)
  );

  if (!fallbackTrailer) {
    return null;
  }

  return {
    ...fallbackTrailer,
    url: getTrailerUrl(fallbackTrailer),
  };
}

function normalizeMovie(movie, genresById = {}) {
  if (!movie) {
    return null;
  }

  const genreNames = Array.isArray(movie.genre_ids)
    ? movie.genre_ids.map((genreId) => genresById[genreId]).filter(Boolean)
    : buildGenreNames(movie);

  return {
    id: movie.id,
    title: movie.title,
    originalTitle: movie.original_title,
    overview: movie.overview,
    posterPath: movie.poster_path,
    backdropPath: movie.backdrop_path,
    rating: movie.vote_average,
    voteCount: movie.vote_count,
    popularity: movie.popularity,
    releaseDate: movie.release_date,
    releaseYear: getReleaseYear(movie.release_date),
    adult: movie.adult,
    genreNames,
    genres: movie.genres || [],
    mediaType: movie.media_type || "movie",
  };
}

export async function getCatalogData() {
  const [
    genresResponse,
    trendingResponse,
    topRatedResponse,
    popularResponse,
    nowPlayingResponse,
    upcomingResponse,
  ] = await Promise.all([
    api.get("genre/movie/list"),
    api.get("trending/movie/week"),
    api.get("movie/top_rated", { params: { page: 1 } }),
    api.get("movie/popular", { params: { page: 1 } }),
    api.get("movie/now_playing", { params: { page: 1 } }),
    api.get("movie/upcoming", { params: { page: 1 } }),
  ]);

  const genres = genresResponse.data.genres || [];
  const genresById = mapGenresById(genres);

  const curatedGenreRequests = CURATED_GENRES.map((genre) => {
    const matchedGenre = genres.find((item) => item.name === genre.genreName);

    if (!matchedGenre) {
      return Promise.resolve({ data: { results: [] }, meta: genre });
    }

    return api
      .get("discover/movie", {
        params: {
          page: 1,
          sort_by: "popularity.desc",
          with_genres: matchedGenre.id,
        },
      })
      .then((response) => ({ ...response, meta: { ...genre, genreId: matchedGenre.id } }));
  });

  const curatedGenreResponses = await Promise.all(curatedGenreRequests);

  const featuredCandidates = [
    ...(trendingResponse.data.results || []),
    ...(popularResponse.data.results || []),
  ]
    .map((movie) => normalizeMovie(movie, genresById))
    .filter((movie) => movie?.backdropPath && movie?.overview);

  const featuredMovie = featuredCandidates[0] || null;

  const sections = [
    {
      id: "trending",
      title: "Trending Now",
      description: "Os titulos mais comentados da semana.",
      items: (trendingResponse.data.results || [])
        .map((movie) => normalizeMovie(movie, genresById))
        .filter(Boolean),
    },
    {
      id: "top-rated",
      title: "Top Rated",
      description: "Filmes com as melhores avaliacoes da comunidade.",
      items: (topRatedResponse.data.results || [])
        .map((movie) => normalizeMovie(movie, genresById))
        .filter(Boolean),
    },
    {
      id: "popular",
      title: "Mais Populares",
      description: "Os blockbusters que puxam a conversa agora.",
      items: (popularResponse.data.results || [])
        .map((movie) => normalizeMovie(movie, genresById))
        .filter(Boolean),
    },
    {
      id: "now-playing",
      title: "Em Cartaz",
      description: "Lancamentos recem-chegados aos cinemas.",
      items: (nowPlayingResponse.data.results || [])
        .map((movie) => normalizeMovie(movie, genresById))
        .filter(Boolean),
    },
    {
      id: "upcoming",
      title: "Proximas Estreias",
      description: "O que vale colocar na sua watchlist desde ja.",
      items: (upcomingResponse.data.results || [])
        .map((movie) => normalizeMovie(movie, genresById))
        .filter(Boolean),
    },
    ...curatedGenreResponses.map((response) => ({
      id: response.meta.slug,
      title: response.meta.title,
      description: `Selecao especial em ${response.meta.genreName}.`,
      genreId: response.meta.genreId,
      items: (response.data.results || [])
        .map((movie) => normalizeMovie(movie, genresById))
        .filter(Boolean),
    })),
  ];

  return {
    featuredMovie,
    sections,
    genres,
    genresById,
  };
}

export async function searchMovies(query, genresById = {}) {
  const response = await api.get("search/movie", {
    params: {
      query,
      page: 1,
    },
  });

  return (response.data.results || [])
    .map((movie) => normalizeMovie(movie, genresById))
    .filter(Boolean);
}

export async function getMoviesByGenre(genreId, genresById = {}) {
  const response = await api.get("discover/movie", {
    params: {
      page: 1,
      sort_by: "popularity.desc",
      with_genres: genreId,
    },
  });

  return (response.data.results || [])
    .map((movie) => normalizeMovie(movie, genresById))
    .filter(Boolean);
}

export async function getMovieDetails(movieId, genresById = {}) {
  const response = await api.get(`movie/${movieId}`, {
    params: {
      append_to_response: "videos,credits,similar",
    },
  });

  const movie = response.data;
  const trailer = pickTrailer(movie.videos?.results || []);

  return {
    ...normalizeMovie(movie, genresById),
    genres: movie.genres || [],
    genreNames: buildGenreNames(movie),
    status: movie.status,
    tagline: movie.tagline,
    homepage: movie.homepage,
    imdbId: movie.imdb_id,
    runtime: movie.runtime,
    budget: movie.budget,
    revenue: movie.revenue,
    trailer,
    cast: (movie.credits?.cast || []).slice(0, 8),
    recommendations: (movie.similar?.results || [])
      .map((item) => normalizeMovie(item, genresById))
      .filter(Boolean),
  };
}
